import { CPLAT_API_ROUTES, sellerQueries, usePost } from '@/api';
import { useLog } from '@/hooks/V3/useLog';
import { useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { ShippingCompany } from '@/types/user-info';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

export const useShippingCompanyPreference = (
  shippingCompanyData: ShippingCompany[] | undefined,
) => {
  const queryClient = useQueryClient();

  const user = useAppSelector((state) => state.user);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const { setUserEventHistory } = useLog(user.UserNo);
  const { mutate: setShippingCompanyList } = usePost(CPLAT_API_ROUTES.setShippingCompanyList);

  // 선호 택배사 코드 리스트 계산
  const preferedShippingCompanyList = useMemo<string[]>(() => {
    if (!shippingCompanyData) return [];
    return shippingCompanyData
      .filter((item) => item.isPrefer)
      .map((item) => item.shippingCompanyCode);
  }, [shippingCompanyData]);

  // 선호 택배사 설정 핸들러
  const handlePrefer = useCallback(
    (selectedCode: string) => {
      const updatedList = preferedShippingCompanyList.includes(selectedCode)
        ? preferedShippingCompanyList.filter((code) => code !== selectedCode)
        : [...preferedShippingCompanyList, selectedCode];

      setShippingCompanyList(
        {
          cplatToken: user.CplatToken,
          PreferShippingCompanyList: updatedList,
        },
        {
          onSuccess: () => {
            setUserEventHistory({
              eventName: 'set_prefered_shipping_company',
              eventValue: selectedCode,
              deviceUuid,
            });
            queryClient.invalidateQueries({ queryKey: sellerQueries.all() });
          },
        },
      );
    },
    [preferedShippingCompanyList, user, deviceUuid, setShippingCompanyList, setUserEventHistory],
  );

  return { preferedShippingCompanyList, handlePrefer };
};
